import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { getPaymentStatus } from "../lib/api";
import { setSubscriber } from "../lib/paywall";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | failed
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const res = await getPaymentStatus(sessionId);
        if (res.payment_status === "paid") {
          setSubscriber(true);
          setState("paid");
          return;
        }
      } catch { /* retry */ }
      if (attempts.current >= 8) { setState("failed"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="paper-texture flex min-h-[70vh] items-center justify-center px-4 py-16">
      <div className="w-full max-w-md rounded-2xl border border-[var(--paper-border)] bg-white p-10 text-center" data-testid="payment-result">
        {state === "checking" && (
          <>
            <Loader2 className="mx-auto animate-spin text-[var(--gold)]" size={44} />
            <h1 className="font-display mt-4 text-2xl font-bold text-[var(--navy)]">Confirming your subscription…</h1>
            <p className="font-serif-body mt-2 text-slate-600">One moment while we verify your payment with Stripe.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="mx-auto text-green-600" size={48} />
            <h1 className="font-display mt-4 text-3xl font-bold text-[var(--navy)]">Welcome, subscriber!</h1>
            <p className="font-serif-body mt-2 text-slate-600">
              Your digital subscription is active. Every story on the News-Leader is now unlocked for you.
            </p>
            <Link to="/" data-testid="success-home-btn" className="font-ui mt-6 inline-block rounded-full bg-[var(--gold)] px-6 py-3 text-xs font-semibold uppercase tracking-wider text-white hover:bg-[#b08f48]">
              Start Reading
            </Link>
          </>
        )}
        {state === "failed" && (
          <>
            <XCircle className="mx-auto text-[var(--breaking)]" size={48} />
            <h1 className="font-display mt-4 text-2xl font-bold text-[var(--navy)]">We couldn't confirm payment</h1>
            <p className="font-serif-body mt-2 text-slate-600">If you were charged, your access will activate shortly. Otherwise, please try again.</p>
            <Link to="/subscribe" className="font-ui mt-6 inline-block rounded-full bg-[var(--navy)] px-6 py-3 text-xs font-semibold uppercase tracking-wider text-white">
              Back to Plans
            </Link>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="paper-texture flex min-h-[70vh] items-center justify-center px-4 py-16">
      <div className="w-full max-w-md rounded-2xl border border-[var(--paper-border)] bg-white p-10 text-center">
        <XCircle className="mx-auto text-slate-400" size={48} />
        <h1 className="font-display mt-4 text-2xl font-bold text-[var(--navy)]">Checkout canceled</h1>
        <p className="font-serif-body mt-2 text-slate-600">No worries — you can subscribe whenever you're ready. The News-Leader stays free to browse.</p>
        <Link to="/subscribe" className="font-ui mt-6 inline-block rounded-full bg-[var(--gold)] px-6 py-3 text-xs font-semibold uppercase tracking-wider text-white hover:bg-[#b08f48]">
          View Plans Again
        </Link>
      </div>
    </div>
  );
}
